import React, { useState, useRef, useEffect } from 'react';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import 'primereact/resources/themes/lara-light-indigo/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import apiClient from '../../stateManagers/services/axiosInterceptor';
import { useDefaultDataStore } from '../../stateManagers/store/useDefaultDataStore';

interface CsvSettings {
  subjects: string;
  classes: string;
  feesStructure: string;
}

interface ApiResponse {
  status: string;
  message: string;
  umeta_id?: number;
}

export default function Settings() {
  // Get data from Zustand store
  const { csv_settings, loading: storeLoading, error: storeError, fetchDefaultData } = useDefaultDataStore();

  const [csvUrls, setCsvUrls] = useState<CsvSettings>({
    subjects: '',
    classes: '',
    feesStructure: '',
  });
  const [editingField, setEditingField] = useState<keyof CsvSettings | null>(null);
  const [tempValue, setTempValue] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const toast = useRef<Toast>(null);

  // Assume user_id is available (e.g., from context or auth)
  const userId = 1; // Replace with actual user_id from context or auth

  // Fetch default data on component mount
  useEffect(() => {
    fetchDefaultData();
  }, [fetchDefaultData]);

  // Update local state when store data changes
  useEffect(() => {
    if (csv_settings) {
      setCsvUrls({
        subjects: (csv_settings as any)['subjects.csv'] || csv_settings.subjects_csv_url || '',
        classes: (csv_settings as any)['classes.csv'] || csv_settings.classes_csv_url || '',
        feesStructure: (csv_settings as any)['fees_structure.csv'] || csv_settings.fees_structure_csv_url || '',
      });
    }
  }, [csv_settings]);

  // Show error toast if store has error
  useEffect(() => {
    if (storeError) {
      toast.current?.show({
        severity: 'error',
        summary: 'Loading Error',
        detail: storeError,
        life: 5000,
      });
    }
  }, [storeError]);

  const styles = `
    @keyframes fadeInSlide {
      from {
        opacity: 0;
        transform: translateY(20px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    @keyframes pulse {
      0%, 100% {
        opacity: 1;
      }
      50% {
        opacity: 0.5;
      }
    }

    .settings-input:focus {
      border-color: #2855ae !important;
      box-shadow: 0 0 0 3px rgba(40, 85, 174, 0.1) !important;
    }

    .settings-button {
      transition: all 0.2s ease;
    }

    .settings-button:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4) !important;
    }

    .info-card {
      animation: fadeInSlide 0.5s ease-out;
    }

    .skeleton {
      background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
      background-size: 200% 100%;
      animation: loading 1.5s ease-in-out infinite;
    }

    @keyframes loading {
      0% {
        background-position: 200% 0;
      }
      100% {
        background-position: -200% 0;
      }
    }
  `;

  const handleEdit = (field: keyof CsvSettings) => {
    setEditingField(field);
    setTempValue(csvUrls[field]);
  };

  const handleSave = async (field: keyof CsvSettings) => {
    if (!tempValue.trim()) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Please enter a valid CSV URL',
        life: 3000,
      });
      return;
    }

    // Basic URL validation
    try {
      new URL(tempValue);
    } catch (error) {
      toast.current?.show({
        severity: 'error',
        summary: 'Invalid URL',
        detail: 'Please enter a valid URL format',
        life: 3000,
      });
      return;
    }

    setIsSaving(true);

    try {
      // Map field to meta_key
      const metaKeyMap: { [key in keyof CsvSettings]: string } = {
        subjects: 'subjects_csv_url',
        classes: 'classes_csv_url',
        feesStructure: 'fees_structure_csv_url',
      };

      // Make API call to save user meta
      const response = await apiClient.post<ApiResponse>('/superadmin/usermeta', {
        user_id: userId,
        meta_key: metaKeyMap[field],
        meta_value: tempValue,
      });

      if (response.data.status === 'success') {
        setCsvUrls((prev) => ({
          ...prev,
          [field]: tempValue,
        }));
        
        // Refresh the store to get updated values
        await fetchDefaultData();
        
        toast.current?.show({
          severity: 'success',
          summary: 'Settings Saved',
          detail: 'CSV URL has been updated successfully',
          life: 3000,
        });
        setEditingField(null);
        setTempValue('');
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Save Failed',
          detail: response.data.message || 'Failed to save CSV URL',
          life: 3000,
        });
      }
    } catch (error: any) {
      console.error('Error saving CSV URL:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Save Failed',
        detail: error.response?.data?.message || 'Failed to save CSV URL',
        life: 3000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditingField(null);
    setTempValue('');
  };

  const handleDownload = async (field: keyof CsvSettings) => {
    const url = csvUrls[field];
    if (!url) {
      toast.current?.show({
        severity: 'warn',
        summary: 'No URL Set',
        detail: 'Please configure the CSV URL first',
        life: 3000,
      });
      return;
    }

    try {
      // Map field to the expected filename
      const filenameMap: { [key in keyof CsvSettings]: string } = {
        subjects: 'subjects.csv',
        classes: 'classes.csv',
        feesStructure: 'fees_structure.csv',
      };
      
      const filename = filenameMap[field];
      
      // Call the download API
      const response = await apiClient.get('/superadmin/download-csv', {
        params: { filename },
        responseType: 'blob',
      });

      // Create a download link
      const blob = new Blob([response.data], { type: 'text/csv' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      toast.current?.show({
        severity: 'success',
        summary: 'Download Started',
        detail: 'CSV file is being downloaded',
        life: 3000,
      });
    } catch (error: any) {
      console.error('Error downloading CSV:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Download Failed',
        detail: error.response?.data?.error || 'Failed to download CSV file',
        life: 3000,
      });
    }
  };

  const csvConfigs = [
    {
      key: 'subjects' as keyof CsvSettings,
      title: 'Subjects Sample CSV',
      icon: 'pi pi-book',
      placeholder: 'https://example.com/subjects-sample.csv',
      iconColor: '#3b82f6',
      iconBg: '#dbeafe',
    },
    {
      key: 'classes' as keyof CsvSettings,
      title: 'Classes Sample CSV',
      icon: 'pi pi-graduation-cap',
      placeholder: 'https://example.com/classes-sample.csv',
      iconColor: '#8b5cf6',
      iconBg: '#ede9fe',
    },
    {
      key: 'feesStructure' as keyof CsvSettings,
      title: 'Fees Structure Sample CSV',
      icon: 'pi pi-dollar',
      placeholder: 'https://example.com/fees-structure-sample.csv',
      iconColor: '#10b981',
      iconBg: '#d1fae5',
    },
  ];

  // Loading skeleton
  if (storeLoading) {
    return (
      <div style={{ minHeight: '100vh', padding: '0px', background: '#fdfdfdff' }}>
        <style>{styles}</style>
        <Toast ref={toast} />

        {/* Header Skeleton */}
        <div
          className="skeleton"
          style={{
            borderRadius: '24px',
            height: '120px',
            marginBottom: '24px',
          }}
        />

        {/* Info Card Skeleton */}
        <div
          className="skeleton"
          style={{
            borderRadius: '16px',
            height: '100px',
            marginBottom: '24px',
          }}
        />

        {/* CSV Settings Skeleton */}
        <div
          style={{
            background: 'white',
            borderRadius: '20px',
            padding: '28px',
            marginBottom: '24px',
            boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
            border: '2px solid #dbeafe',
          }}
        >
          {[1, 2, 3].map((i) => (
            <div key={i} className="skeleton" style={{ height: '80px', borderRadius: '12px', marginBottom: '16px' }} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', padding: '0px', background: '#fdfdfdff' }}>
      <style>{styles}</style>
      <Toast ref={toast} />

      {/* Header */}
      <div
        style={{
          background: 'linear-gradient(135deg, #2855ae 0%, #3b82f6 100%)',
          borderRadius: '24px',
          padding: '32px',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              borderRadius: '16px',
              padding: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <i className="pi pi-cog" style={{ fontSize: '18px', color: 'white' }}></i>
          </div>
          <div>
            <h1
              style={{
                color: 'white',
                fontSize: '28px',
                fontWeight: '600',
                margin: '0 0 4px 0',
              }}
            >
              Settings
            </h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '14px' }}>
                {new Date().toISOString().split('T')[0]}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Info Card */}
      <div
        style={{
          background: 'white',
          borderRadius: '16px',
          padding: '20px',
          marginBottom: '24px',
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
          border: '2px solid #dbeafe',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
          <div
            style={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              borderRadius: '10px',
              padding: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: '32px',
              height: '32px',
            }}
          >
            <i className="pi pi-info-circle" style={{ fontSize: '14px', color: 'white' }}></i>
          </div>
          <div style={{ flex: 1 }}>
            <h4
              style={{
                color: '#2855ae',
                fontSize: '14px',
                fontWeight: '600',
                margin: '0 0 8px 0',
              }}
            >
              CSV Configuration
            </h4>
            <div style={{ color: '#6b7280', fontSize: '13px', lineHeight: '1.6' }}>
              Configure the URLs for CSV sample templates. These templates will be used for bulk data uploads throughout the application.
            </div>
          </div>
        </div>
      </div>

      {/* CSV Settings Card */}
      <div
        className="info-card"
        style={{
          background: 'white',
          borderRadius: '20px',
          padding: '28px',
          marginBottom: '24px',
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
          border: '2px solid #dbeafe',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
          <div
            style={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              borderRadius: '10px',
              padding: '8px',
              minWidth: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <i className="pi pi-link" style={{ fontSize: '14px', color: 'white' }}></i>
          </div>
          <h3
            style={{
              color: '#2855ae',
              fontSize: '16px',
              fontWeight: '600',
              margin: '0',
            }}
          >
            Sample CSV URLs
          </h3>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {csvConfigs.map((config, index) => {
            const isEditing = editingField === config.key;
            const value = isEditing ? tempValue : csvUrls[config.key];

            return (
              <div
                key={config.key}
                style={{
                  background: '#f9fafb',
                  borderRadius: '12px',
                  padding: '20px',
                  border: '1px solid #e5e7eb',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                  <div
                    style={{
                      background: config.iconBg,
                      borderRadius: '8px',
                      padding: '8px',
                      minWidth: '32px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <i className={config.icon} style={{ fontSize: '14px', color: config.iconColor }}></i>
                  </div>
                  <label
                    style={{
                      color: '#2855ae',
                      fontSize: '14px',
                      fontWeight: '600',
                      flex: 1,
                    }}
                  >
                    {config.title}
                  </label>
                  <span
                    style={{
                      background: csvUrls[config.key] ? '#dcfce7' : '#fee2e2',
                      color: csvUrls[config.key] ? '#15803d' : '#991b1b',
                      padding: '4px 10px',
                      borderRadius: '6px',
                      fontSize: '11px',
                      fontWeight: '600',
                    }}
                  >
                    {csvUrls[config.key] ? 'Configured' : 'Not Set'}
                  </span>
                </div>

                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <div style={{ flex: 1 }}>
                    <InputText
                      value={value}
                      onChange={(e) => setTempValue(e.target.value)}
                      disabled={!isEditing}
                      placeholder={config.placeholder}
                      className="settings-input"
                      style={{
                        width: '100%',
                        border: isEditing ? '2px solid #2855ae' : '1px solid #e5e7eb',
                        borderRadius: '8px',
                        padding: '10px 12px',
                        fontSize: '13px',
                        background: isEditing ? 'white' : '#f9fafb',
                        color: '#1f2937',
                        transition: 'all 0.2s',
                      }}
                    />
                  </div>

                  {!isEditing ? (
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => handleEdit(config.key)}
                        className="settings-button"
                        style={{
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          color: '#3b82f6',
                          transition: 'all 0.2s',
                          padding: '8px',
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.color = '#2563eb';
                          e.currentTarget.style.transform = 'scale(1.1)';
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.color = '#3b82f6';
                          e.currentTarget.style.transform = 'scale(1)';
                        }}
                      >
                        <i className="pi pi-pencil" style={{ fontSize: '14px' }}></i>
                      </button>
                      <button
                        onClick={() => handleDownload(config.key)}
                        disabled={!csvUrls[config.key]}
                        className="settings-button"
                        style={{
                          background: 'transparent',
                          border: 'none',
                          cursor: csvUrls[config.key] ? 'pointer' : 'not-allowed',
                          color: csvUrls[config.key] ? '#10b981' : '#9ca3af',
                          transition: 'all 0.2s',
                          padding: '8px',
                          opacity: csvUrls[config.key] ? 1 : 0.5,
                        }}
                        onMouseOver={(e) => {
                          if (csvUrls[config.key]) {
                            e.currentTarget.style.color = '#059669';
                            e.currentTarget.style.transform = 'scale(1.1)';
                          }
                        }}
                        onMouseOut={(e) => {
                          if (csvUrls[config.key]) {
                            e.currentTarget.style.color = '#10b981';
                            e.currentTarget.style.transform = 'scale(1)';
                          }
                        }}
                      >
                        <i className="pi pi-download" style={{ fontSize: '14px' }}></i>
                      </button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                      <button
                        onClick={() => handleSave(config.key)}
                        disabled={isSaving}
                        className="settings-button"
                        style={{
                          background: isSaving ? '#9ca3af' : '#10b981',
                          border: 'none',
                          borderRadius: '8px',
                          width: '32px',
                          height: '32px',
                          cursor: isSaving ? 'not-allowed' : 'pointer',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
                        }}
                      >
                        <i className={isSaving ? 'pi pi-spin pi-spinner' : 'pi pi-check'} style={{ fontSize: '12px' }}></i>
                      </button>
                      <button
                        onClick={handleCancel}
                        disabled={isSaving}
                        style={{
                          background: '#ef4444',
                          border: 'none',
                          borderRadius: '8px',
                          width: '32px',
                          height: '32px',
                          cursor: isSaving ? 'not-allowed' : 'pointer',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 2px 8px rgba(239, 68, 68, 0.3)',
                          opacity: isSaving ? 0.6 : 1,
                        }}
                      >
                        <i className="pi pi-times" style={{ fontSize: '12px' }}></i>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}